import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CircleNotch, Buildings } from "@phosphor-icons/react";
import { HUB_TYPE_LABELS, HUB_INDUSTRY_LABELS, HUB_TYPE_INDUSTRY } from "@/lib/hubEnums";

const STATUS_LABEL = {
  created_new: "New customer created in Admin Hub",
  attached_existing: "Award attached to the existing Admin Hub customer",
  exists: "Admin Hub already holds this award (duplicate-safe)",
  needs_review: "Admin Hub flagged the handoff for review",
};

const Fld = ({ label, children }) => (
  <label className="block text-xs font-semibold text-[#52525B]">
    {label}
    <div className="mt-1">{children}</div>
  </label>
);

function LocationFields({ value, onChange, prefill }) {
  const v = value || {};
  const set = (k) => (e) => onChange({ name: prefill.name, street_address: prefill.street_address,
    city: prefill.city, state: prefill.state, zip: prefill.zip_code, ...v, [k]: e.target.value });
  return (
    <div className="mt-2 grid grid-cols-2 gap-2">
      <Input placeholder="Location name" defaultValue={prefill.name || ""} onChange={set("name")} className="col-span-2 rounded-sm text-xs" data-testid="handoff-newloc-name" />
      <Input placeholder="Street address" defaultValue={prefill.street_address || ""} onChange={set("street_address")} className="col-span-2 rounded-sm text-xs" data-testid="handoff-newloc-address" />
      <Input placeholder="City" defaultValue={prefill.city || ""} onChange={set("city")} className="rounded-sm text-xs" />
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="State" defaultValue={prefill.state || ""} onChange={set("state")} className="rounded-sm text-xs" />
        <Input placeholder="ZIP" defaultValue={prefill.zip_code || ""} onChange={set("zip")} className="rounded-sm text-xs" />
      </div>
    </div>
  );
}

// Administrator-driven Admin Hub award transfer. Candidates are never
// auto-selected; new customers/locations require explicit confirmation.
export default function HandoffDialog({ open, opportunity, onClose, onDone }) {
  const [step, setStep] = useState("match"); // match | locations | new_customer | result
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [match, setMatch] = useState(null);           // {candidates, awarded_properties, known_customer_id}
  const [choice, setChoice] = useState("");            // candidate customer_id | "__new__"
  const [hubLocations, setHubLocations] = useState([]);
  const [locSel, setLocSel] = useState({});            // property_id -> {location_id} | {new_location}
  const [customer, setCustomer] = useState({ name: "", type: "", industry: "" });
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep("match"); setChoice(""); setLocSel({}); setResult(null);
    setLoading(true);
    api.post(`/opportunities/${opportunity.id}/handoff/match`)
      .then(({ data }) => {
        setMatch(data);
        setCustomer((c) => ({ ...c, name: data.organization_name || "" }));
        if (data.known_customer_id) setChoice(data.known_customer_id);
      })
      .catch((e) => { toast.error(e.response?.data?.detail || "Admin Hub match failed"); onClose(); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opportunity.id]);

  const toLocations = useCallback(async () => {
    if (choice === "__new__") { setStep("new_customer"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/opportunities/${opportunity.id}/handoff/locations/${choice}`);
      setHubLocations(data.locations || []);
      setStep("locations");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Admin Hub locations");
    } finally { setLoading(false); }
  }, [choice, opportunity.id]);

  const send = useCallback(async () => {
    const props = match?.awarded_properties || [];
    const locations = props.map((p) => {
      const sel = locSel[p.id] || {};
      if (choice === "__new__" || sel.mode === "new") {
        return { reach_property_id: p.id, new_location: sel.new_location || {
          name: p.name, street_address: p.street_address, city: p.city, state: p.state, zip: p.zip_code } };
      }
      return { reach_property_id: p.id, location_id: sel.location_id };
    });
    const body = choice === "__new__"
      ? { customer: { name: customer.name }, force_new: true, locations }
      : { customer_id: choice, locations };
    setSending(true);
    try {
      const { data } = await api.post(`/opportunities/${opportunity.id}/handoff/send`, body);
      setResult(data); setStep("result");
      if (data.handoff_status === "complete") toast.success("Award transferred to Admin Hub");
      else toast.warning("Admin Hub flagged the handoff for review");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Handoff failed — the approved close is unaffected");
      onDone?.();
    } finally { setSending(false); }
  }, [choice, customer, locSel, match, opportunity.id, onDone]);

  const props = match?.awarded_properties || [];
  const locationsReady = choice === "__new__"
    || props.every((p) => (locSel[p.id]?.location_id) || (locSel[p.id]?.mode === "new"));

  // Properties that will be sent as NEW Hub locations but whose REACH Market
  // Segment has no agreed Admin Hub location-industry mapping. Industry is never
  // guessed — the Hub keeps such transfers in Needs Review until the mapping is agreed.
  const unmappedNewLocs = props.filter((p) =>
    (choice === "__new__" || locSel[p.id]?.mode === "new") && !p.hub_location_industry);
  const MappingNotice = () => unmappedNewLocs.length === 0 ? null : (
    <p className="rounded-sm bg-[#F59E0B]/10 px-3 py-2 text-xs font-semibold text-[#B45309]" data-testid="handoff-industry-mapping-notice">
      {unmappedNewLocs.map((p) => p.market_segment
        ? `Market Segment “${p.market_segment}” (${p.name})`
        : `No Market Segment set on “${p.name}”`).join("; ")} has no agreed Admin Hub
      location-industry mapping. The transfer sends the saved county and address details,
      but the Hub will keep it in Needs Review until the mapping is agreed — industry is
      never guessed and the customer-level Industry is never substituted.
    </p>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="handoff-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-bold text-[#032852]">
            <Buildings size={20} className="text-[#569A1F]" /> Admin Hub Transfer
          </DialogTitle>
          <DialogDescription>
            Admin Hub owns customer and location master records. Select the correct match —
            nothing is auto-selected, and retries never create duplicates.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="flex justify-center py-8"><CircleNotch size={24} className="animate-spin text-[#032852]" /></div>}

        {!loading && step === "match" && match && (
          <div className="space-y-2" data-testid="handoff-step-match">
            <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">
              Customer candidates for “{match.organization_name}”
            </p>
            {(match.candidates || []).length === 0 && (
              <p className="rounded-sm bg-[#FAFAFA] px-3 py-2 text-xs text-[#52525B]">No existing Admin Hub customer matched.</p>
            )}
            {(match.candidates || []).map((c) => (
              <label key={c.customer_id} className={`flex cursor-pointer items-start gap-2 rounded-sm border px-3 py-2 text-xs ${choice === c.customer_id ? "border-[#569A1F] bg-[#569A1F]/5" : "border-[#E4E4E7]"}`}
                data-testid={`handoff-candidate-${c.customer_id}`}>
                <input type="radio" name="hub-cust" className="mt-0.5" checked={choice === c.customer_id} onChange={() => setChoice(c.customer_id)} />
                <span>
                  <span className="font-bold text-[#032852]">{c.name}</span>
                  {c.score != null && <span className="ml-2 rounded-sm bg-[#032852]/10 px-1.5 py-0.5 font-semibold">{c.score}% match</span>}
                  {c.address && <span className="block text-[#52525B]">{c.address}</span>}
                  {(c.reasons || []).length > 0 && <span className="block text-[#6D6D70]">{c.reasons.join(" · ")}</span>}
                </span>
              </label>
            ))}
            <label className={`flex cursor-pointer items-center gap-2 rounded-sm border px-3 py-2 text-xs ${choice === "__new__" ? "border-[#032852] bg-[#032852]/5" : "border-[#E4E4E7]"}`}>
              <input type="radio" name="hub-cust" checked={choice === "__new__"} onChange={() => setChoice("__new__")} data-testid="handoff-choose-new-customer" />
              <span className="font-semibold text-[#032852]">This is a NEW customer — create it in Admin Hub</span>
            </label>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
              <Button disabled={!choice} onClick={toLocations} className="rounded-sm bg-[#032852] text-white" data-testid="handoff-match-continue">Continue</Button>
            </DialogFooter>
          </div>
        )}

        {!loading && step === "locations" && (
          <div className="space-y-3" data-testid="handoff-step-locations">
            <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Match each awarded service location</p>
            {props.map((p) => {
              const sel = locSel[p.id] || {};
              return (
                <div key={p.id} className="rounded-sm border border-[#E4E4E7] px-3 py-2 text-xs" data-testid={`handoff-loc-${p.id}`}>
                  <p className="font-bold text-[#032852]">{p.name}{p.street_address ? ` — ${p.street_address}` : ""}</p>
                  <select className="mt-1 w-full rounded-sm border border-[#E4E4E7] bg-white px-2 py-1.5 text-xs"
                    value={sel.mode === "new" ? "__newloc__" : (sel.location_id || "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      setLocSel((s) => ({ ...s, [p.id]: v === "__newloc__" ? { mode: "new" } : { location_id: v } }));
                    }} data-testid={`handoff-loc-select-${p.id}`}>
                    <option value="">Select the Admin Hub location…</option>
                    {hubLocations.map((l) => (
                      <option key={l.location_id} value={l.location_id}>{l.name}{l.address ? ` — ${l.address}` : ""}</option>
                    ))}
                    <option value="__newloc__">Add as a NEW location under this customer</option>
                  </select>
                  {sel.mode === "new" && (
                    <LocationFields prefill={p} value={sel.new_location}
                      onChange={(nl) => setLocSel((s) => ({ ...s, [p.id]: { mode: "new", new_location: nl } }))} />
                  )}
                </div>
              );
            })}
            <MappingNotice />
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setStep("match")}>Back</Button>
              <Button disabled={!locationsReady || sending} onClick={send} className="rounded-sm bg-[#569A1F] font-semibold text-white" data-testid="handoff-send-btn">
                {sending ? <CircleNotch size={18} className="animate-spin" /> : "Send to Admin Hub"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {!loading && step === "new_customer" && (() => {
          const t = match?.hub_customer_type, ind = match?.hub_customer_industry;
          const missing = !t || !ind;
          const mismatch = !missing && HUB_TYPE_INDUSTRY[t] !== ind;
          const blocked = missing || mismatch;
          return (
            <div className="space-y-3" data-testid="handoff-step-new-customer">
              <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">New Admin Hub customer — confirm the details</p>
              <Fld label="Customer name"><Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="rounded-sm" data-testid="handoff-customer-name" /></Fld>
              {missing && (
                <p className="rounded-sm bg-[#EF4444]/10 px-3 py-2 text-xs font-semibold text-[#B91C1C]" data-testid="handoff-classification-missing">
                  Customer {!t && !ind ? "Type and Industry are" : !t ? "Type is" : "Industry is"} not set on the organization record.
                  Set it via the organization's Edit dialog, then reopen this transfer — values are never entered here or guessed.
                </p>
              )}
              {mismatch && (
                <p className="rounded-sm bg-[#EF4444]/10 px-3 py-2 text-xs font-semibold text-[#B91C1C]" data-testid="handoff-classification-mismatch">
                  Incompatible pair on the organization record: Type "{HUB_TYPE_LABELS[t] || t}" must pair with Industry
                  "{HUB_INDUSTRY_LABELS[HUB_TYPE_INDUSTRY[t]] || HUB_TYPE_INDUSTRY[t]}", not "{HUB_INDUSTRY_LABELS[ind] || ind}".
                  Correct the organization record, then retry.
                </p>
              )}
              {!blocked && (
                <div className="grid grid-cols-2 gap-2">
                  <Fld label="Type (from organization record)">
                    <p className="rounded-sm border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2 text-sm font-semibold text-[#032852]" data-testid="handoff-customer-type">{HUB_TYPE_LABELS[t]}</p>
                  </Fld>
                  <Fld label="Industry (from organization record)">
                    <p className="rounded-sm border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2 text-sm font-semibold text-[#032852]" data-testid="handoff-customer-industry">{HUB_INDUSTRY_LABELS[ind]}</p>
                  </Fld>
                </div>
              )}
              <p className="text-[11px] text-[#6D6D70]">Awarded locations are sent with their REACH details: {props.map((p) => p.name).join(", ") || "—"}. Missing values are never guessed — edit them in the organization record first if needed.</p>
              <MappingNotice />
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setStep("match")}>Back</Button>
                <Button disabled={!customer.name || blocked || sending} onClick={send} className="rounded-sm bg-[#569A1F] font-semibold text-white" data-testid="handoff-create-send-btn">
                  {sending ? <CircleNotch size={18} className="animate-spin" /> : "Create Customer & Send Award"}
                </Button>
              </DialogFooter>
            </div>
          );
        })()}

        {!loading && step === "result" && result && (
          <div className="space-y-2" data-testid="handoff-step-result">
            <p className={`rounded-sm px-3 py-2 text-sm font-semibold ${result.handoff_status === "complete" ? "bg-[#569A1F]/10 text-[#2f5a0e]" : "bg-[#F59E0B]/10 text-[#B45309]"}`}>
              {STATUS_LABEL[result.result?.status] || `Transfer status: ${result.handoff_status}`}
            </p>
            {result.result?.customer_id && <p className="text-xs text-[#52525B]">Admin Hub customer ID: <span className="font-mono font-semibold">{result.result.customer_id}</span></p>}
            {(result.result?.missing_fields || []).length > 0 && (
              <p className="text-xs text-[#B45309]">Hub needs: {result.result.missing_fields.join(", ")} — correct the selections and retry.</p>
            )}
            {result.mapping_note && (
              <p className="rounded-sm bg-[#F59E0B]/10 px-3 py-2 text-xs text-[#B45309]" data-testid="handoff-mapping-note">{result.mapping_note}</p>
            )}
            <DialogFooter><Button className="rounded-sm bg-[#032852] text-white" onClick={onClose} data-testid="handoff-done-btn">Done</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
